import dependencies from "../authentication/dependencies";
import { BASE_URL } from "../config";
import { uploadFileHeader } from "../cycles/headers";
import v4 from "uuid/v4";
import { postJson } from "./apiErrors";

const { baseUrl, newBaseUrl, generalHeader } = dependencies;

export const fetchTickets = async (csrfToken, userId, filters = {}) => {
  const url = newBaseUrl + "/SupportTicket/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      supportTicketStateId: filters.supportTicketStateId || null,
      supportTicketPriorityId: filters.supportTicketPriorityId || null,
      assignedTo: filters.assignedTo || null,
      reportedBy: filters.reportedBy || null,
      creatorId: filters.creatorId || null,
      searchTerm: filters.searchTerm || null,
      pageNumber: filters.pageNumber || 1,
      pageSize: filters.pageSize || 50,
    },
  };

  const res = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  // Return in compatible format: the list rows read the same field names as the ticket page
  // (fetchTicketById below); the raw Core fields left Status/Severity/Environment empty.
  if (Array.isArray(data?.payload)) {
    return data.payload.map((ticket) => ({
      ...ticket,
      severity:
        ticket.severityAsDefinedByTpp || ticket.severityAsDefinedByAsp || "N/A",
      environment: ticket.environmentImpacted || "N/A",
      status: ticket.state?.name,
    }));
  }
  if (data?.payload) {
    return data.payload;
  }

  return [];
};

export const fetchTicketById = async (csrfToken, id, userId) => {
  const url = newBaseUrl + "/SupportTicket/get";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      id: id,
    },
  };

  const res = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (data?.payload) {
    // Map new API field names to old component field names for compatibility
    const ticket = data.payload;
    return {
      ...ticket,
      // Map severity field with default value for null cases
      severity:
        ticket.severityAsDefinedByTpp || ticket.severityAsDefinedByAsp || "N/A",
      // Map environment field with default value for null cases
      environment: ticket.environmentImpacted || "N/A",
      // Map status from state object
      status: ticket.state?.name,
      // Map description field
      description: ticket.shortDescription,
      // Map contact info field
      contact_info: ticket.contactDetails,
      // Map reported by field
      reported_by: ticket.reportedBy,
      // Map messages array with transformed field names
      message_history:
        ticket.messages?.map((msg) => ({
          ...msg,
          uuid: msg.id,
          uid: msg.creatorId,
          body: msg.body,
          created: msg.created,
          // Map attachment to include file data or download URL
          attachment: msg.attachment
            ? {
                fileName: msg.attachment.fileName,
                fileSize: msg.attachment.fileSize,
                url: msg.attachment.fileData
                  ? `data:application/octet-stream;base64,${msg.attachment.fileData}`
                  : `/Attachment/download/${msg.attachmentId}`,
              }
            : null,
        })) || [],
    };
  }

  throw new Error("Support ticket not found");
};

export const updateTicketById = async (csrfToken, userId, ticketData) => {
  const url = newBaseUrl + "/SupportTicket/update";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: ticketData,
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const createTicket = async (csrfToken, userId, ticketData) => {
  const url = newBaseUrl + "/SupportTicket/create";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: ticketData,
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const deleteTicket = async (csrfToken, userId, ticketId) => {
  const url = newBaseUrl + "/SupportTicket/delete";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      id: ticketId,
    },
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const createTicketMessage = async (csrfToken, userId, messageData) => {
  const url = newBaseUrl + "/SupportTicket/message/create";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: messageData,
  };

  return postJson(url, {
    headers: generalHeader(csrfToken),
    body: payload,
    category: url,
  });
};

export const fetchTicketMessages = async (csrfToken, userId, ticketId) => {
  const url = newBaseUrl + "/SupportTicket/message/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      id: ticketId,
    },
  };

  const res = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (data?.payload) {
    return data.payload;
  }

  return [];
};

export const fetchTicketStates = async (csrfToken, userId) => {
  const url = newBaseUrl + "/SupportTicket/state/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {},
  };

  const res = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (data?.payload) {
    return data.payload;
  }

  return [];
};

export const fetchTicketPriorities = async (csrfToken, userId) => {
  const url = newBaseUrl + "/SupportTicket/priority/list";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {},
  };

  const res = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (data?.payload) {
    return data.payload;
  }

  return [];
};

export const uploadFile = async (csrfToken, file, userId) => {
  // Convert file to base64
  const fileData = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1]; // Remove data:...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const url = newBaseUrl + "/Attachment/create";

  const payload = {
    header: {
      id: v4(),
      application: v4(),
      bank: "NBG",
      userId: userId || v4(),
    },
    payload: {
      fileName: file.name,
      fileData: fileData,
    },
  };

  const response = await fetch(url, {
    method: "POST",
    headers: generalHeader(csrfToken),
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  return data.payload; // Return the attachment UUID
};
